import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  IconButton,
  Fab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Remove as RemoveIcon, ManageAccounts, RestartAlt, Search as SearchIcon, Clear as ClearIcon, PersonOff as PersonOffIcon } from '@mui/icons-material';
import { authService } from '../api/authService';
import { apiRequest } from '../api/api';
import { activeUsersService } from '../api/services';

// Colore della pagina Gestione Dipendenti
const PAGE_COLOR = '#FF9800';

// Componente per input numerico con pulsanti + e -
interface NumericInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

const NumericInput: React.FC<NumericInputProps> = ({ label, value, onChange, min = 0, max = 24 }) => {
  const handleIncrement = () => {
    if (value < max) {
      onChange(Math.min(max, value + 0.5));
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      onChange(Math.max(min, value - 0.5));
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(event.target.value) || 0;
    if (newValue >= min && newValue <= max) {
      onChange(newValue);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
      <Typography variant="body2" sx={{ minWidth: 80 }}>{label}:</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={handleDecrement}
          disabled={value <= min}
          sx={{ 
            width: 28, 
            height: 28,
            bgcolor: PAGE_COLOR,
            color: 'white',
            '&:hover': {
              bgcolor: '#E65100', // Versione più scura di #FF9800
            },
            '&:disabled': {
              bgcolor: 'grey.300',
              color: 'grey.500',
            }
          }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <TextField
          type="number"
          size="small"
          value={value}
          onChange={handleInputChange}
          inputProps={{
            style: { textAlign: 'center', padding: '4px' },
            min,
            max,
            step: 0.5
          }}
          sx={{ 
            width: 60,
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
            '& input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          }}
        />
        <IconButton
          size="small"
          onClick={handleIncrement}
          disabled={value >= max}
          sx={{ 
            width: 28, 
            height: 28,
            bgcolor: PAGE_COLOR,
            color: 'white',
            '&:hover': {
              bgcolor: '#E65100', // Versione più scura di #FF9800
            },
            '&:disabled': {
              bgcolor: 'grey.300',
              color: 'grey.500',
            }
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

interface Dipendente {
  codicePersonale: string;
  neg: string;
  nome: string;
  username?: string;
  oreSettimanali: number;
  livello: number;
  dataAssunzione: string; // ISO string format
  dataFineContratto?: string; // ISO string format
  lunedi: number;
  martedi: number;
  mercoledi: number;
  giovedi: number;
  venerdi: number;
  sabato: number;
  domenica: number;
}

interface Negozio {
  neg: string;
  negozio: string;
}

const DipendenteList: React.FC = () => {
  const [dipendenti, setDipendenti] = useState<Dipendente[]>([]);
  const [negozi, setNegozi] = useState<Negozio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editingDipendente, setEditingDipendente] = useState<Dipendente | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [formData, setFormData] = useState<Dipendente>({
    codicePersonale: '',
    neg: '',
    nome: '',
    oreSettimanali: 40,
    livello: 1,
    dataAssunzione: '',
    dataFineContratto: '',
    lunedi: 8,
    martedi: 8,
    mercoledi: 8,
    giovedi: 8,
    venerdi: 8,
    sabato: 0,
    domenica: 0
  });

  useEffect(() => {
    fetchDipendenti();
    fetchNegozi();
  }, []);

  const fetchDipendenti = async () => {
    try {
      setLoading(true);
      const data = await apiRequest<Dipendente[]>('/dipendenti');
      setDipendenti(data);
      setError(null);
    } catch (err: any) {
      if (err.status === 403) {
        setError('Non hai i permessi per visualizzare i dipendenti');
      } else {
        setError('Errore nel caricamento dei dipendenti');
      }
      console.error('Error fetching dipendenti:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNegozi = async () => {
    try {
      // Otteniamo i negozi accessibili per la dropdown
      const data = await apiRequest<Negozio[]>('/negozi/accessibili');
      setNegozi(data);
    } catch (err) {
      console.error('Error fetching negozi:', err);
      // Non impostiamo errore qui perché è per la dropdown
    }
  };

  const handleOpen = (dipendente?: Dipendente) => {
    if (dipendente) {
      setEditingDipendente(dipendente);
      setFormData(dipendente);
    } else {
      setEditingDipendente(null);
      setFormData({
        codicePersonale: '',
        neg: '',
        nome: '',
        oreSettimanali: 40,
        livello: 1,
        dataAssunzione: '',
        dataFineContratto: '',
        lunedi: 8,
        martedi: 8,
        mercoledi: 8,
        giovedi: 8,
        venerdi: 8,
        sabato: 0,
        domenica: 0
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingDipendente(null);
    setFormData({
      codicePersonale: '',
      neg: '',
      nome: '',
      oreSettimanali: 40,
      livello: 1,
      dataAssunzione: '',
      dataFineContratto: '',
      lunedi: 8,
      martedi: 8,
      mercoledi: 8,
      giovedi: 8,
      venerdi: 8,
      sabato: 0,
      domenica: 0
    });
  };

  const handleSave = async () => {
    try {
      if (editingDipendente) {
        // Update existing dipendente
        await apiRequest(`/dipendenti/${editingDipendente.codicePersonale}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
      } else {
        // Create new dipendente
        await apiRequest('/dipendenti', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
      }
      handleClose();
      fetchDipendenti();
    } catch (err) {
      setError('Errore nel salvare il dipendente');
      console.error('Error saving dipendente:', err);
    }
  };

  const handleDelete = async (codicePersonale: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo dipendente?')) {
      try {
        await apiRequest(`/dipendenti/${codicePersonale}`, {
          method: 'DELETE',
        });
        fetchDipendenti();
      } catch (err) {
        setError('Errore nell\'eliminazione del dipendente');
        console.error('Error deleting dipendente:', err);
      }
    }
  };

  const handleResetPassword = async (codicePersonale: string) => {
    // Conferma azione
    if (!window.confirm('Sei sicuro di voler resettare la password (cancellare NuovaPasswd) per questo dipendente?')) return;
    try {
      // Chiamata generica: backend deve esporre un endpoint che esegue l'azzeramento di NuovaPasswd
      // Endpoint previsto: DELETE /gestione-utenti/{codicePersonale}/nuova-passwd
      await apiRequest(`/gestione-utenti/${codicePersonale}/nuova-passwd`, {
        method: 'DELETE',
      });
      // Refresh parziale: non serve ricaricare tutti i dipendenti
      // La modifica è solo lato gestione_utenti, non impatta i dati visualizzati in questa pagina
      // Mostriamo solo un messaggio di successo
      alert('Password resettata con successo');
    } catch (err) {
      setError('Errore nel resettare la password');
      console.error('Error resetting NuovaPasswd:', err);
    }
  };

  const handleKick = async (codicePersonale: string, nome: string) => {
    if (!window.confirm(`Sloggare "${nome}" (${codicePersonale})?`)) return;
    try {
      await activeUsersService.kickByCodice(codicePersonale);
      alert(`Sessione di "${nome}" terminata`);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        alert('Utente non risulta loggato al momento');
      } else {
        setError('Errore nel terminare la sessione');
      }
    }
  };

  const handleInputChange = (field: keyof Dipendente) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    if (dateString === '2099-12-31' || dateString.includes('2099-12-31')) return 'Indeterminato';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Filtra dipendenti in base al testo di ricerca
  const dipendentiFiltrati = dipendenti.filter((dip) => {
    if (!searchText.trim()) return true;
    const search = searchText.toLowerCase();
    return (
      dip.codicePersonale.toLowerCase().includes(search) ||
      dip.nome.toLowerCase().includes(search) ||
      dip.neg.toLowerCase().includes(search)
    );
  });

  // Raggruppa dipendenti per negozio
  const dipendentiPerNegozio = dipendentiFiltrati.reduce((acc, dipendente) => {
    if (!acc[dipendente.neg]) {
      acc[dipendente.neg] = [];
    }
    acc[dipendente.neg].push(dipendente);
    return acc;
  }, {} as Record<string, Dipendente[]>);

  // Ottieni nome negozio dall'elenco negozi
  const getNomeNegozio = (codiceNegozio: string) => {
    const negozio = negozi.find(n => n.neg === codiceNegozio);
    return negozio ? `${negozio.neg} - ${negozio.negozio}` : codiceNegozio;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography>Caricamento...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <ManageAccounts fontSize="large" sx={{ mr: 2, color: '#FF9800' }} />
          <Typography variant="h4" component="h1">
            Gestione Dipendenti
          </Typography>
        </Box>

        {/* Casella di ricerca in alto a destra */}
        <Box sx={{ 
          position: 'absolute', 
          top: 10, 
          right: 0,
          display: 'flex',
          gap: 1
        }}>
          <TextField
            size="small"
            placeholder="Cerca dipendente..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              bgcolor: 'white',
              borderRadius: 1,
              minWidth: 250,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: PAGE_COLOR,
                },
                '&.Mui-focused fieldset': {
                  borderColor: PAGE_COLOR,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: PAGE_COLOR }} />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchText('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
      
      {!error && (
        <Fab
          aria-label="add"
          onClick={() => handleOpen()}
          size="medium"
          sx={{
            position: 'fixed',
            top: 80,
            right: 20,
            bgcolor: PAGE_COLOR,
            color: 'white',
            '&:hover': {
              bgcolor: '#E65100', // Versione più scura di #FF9800
            },
            zIndex: 1000,
            boxShadow: 3
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {Object.keys(dipendentiPerNegozio).sort().map((codiceNegozio) => (
            <Paper key={codiceNegozio} elevation={2} sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ mb: 2, color: '#FF9800', fontWeight: 'bold' }}>
                {getNomeNegozio(codiceNegozio) || codiceNegozio}
                <Typography variant="subtitle1" component="span" sx={{ ml: 2, color: 'text.secondary' }}>
                  ({dipendentiPerNegozio[codiceNegozio].length} dipendent{dipendentiPerNegozio[codiceNegozio].length === 1 ? 'e' : 'i'}
                  {searchText && ` - filtrati da ${dipendenti.filter(d => d.neg === codiceNegozio).length}`})
                </Typography>
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#FFF8E1' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Codice Personale</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Ore/Sett</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Livello</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Data Assunzione</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Data Fine Contratto</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dipendentiPerNegozio[codiceNegozio].map((dipendente) => (
                      <TableRow key={dipendente.codicePersonale} hover>
                        <TableCell>{dipendente.codicePersonale}</TableCell>
                        <TableCell><strong>{dipendente.nome}</strong></TableCell>
                        <TableCell>{dipendente.oreSettimanali}</TableCell>
                        <TableCell>{dipendente.livello}</TableCell>
                        <TableCell>{formatDate(dipendente.dataAssunzione)}</TableCell>
                        <TableCell>{dipendente.dataFineContratto ? formatDate(dipendente.dataFineContratto) : '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleOpen(dipendente)}
                            size="small"
                            sx={{
                              color: PAGE_COLOR,
                              '&:hover': {
                                bgcolor: 'rgba(255, 152, 0, 0.1)', // Hover con trasparenza
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(dipendente.codicePersonale)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                          {/* Bottone Reset password visibile solo agli Admin */}
                          {(() => {
                            const u = authService.getCurrentUser();
                            if (!(u && u.ruolo === 'Admin')) return null;
                            return (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleResetPassword(dipendente.codicePersonale)}
                                  sx={{
                                    color: PAGE_COLOR,
                                    '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.08)' }
                                  }}
                                  title="Reset password"
                                >
                                  <RestartAlt />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleKick(dipendente.codicePersonale, dipendente.nome)}
                                  title="Forza logout"
                                >
                                  <PersonOffIcon />
                                </IconButton>
                              </>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDipendente ? 'Modifica Dipendente' : 'Nuovo Dipendente'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Codice Personale"
              value={formData.codicePersonale}
              onChange={handleInputChange('codicePersonale')}
              disabled={!!editingDipendente}
              fullWidth
              required
              inputProps={{ maxLength: 20 }}
            />
            
            <FormControl fullWidth required>
              <InputLabel>Negozio</InputLabel>
              <Select
                value={formData.neg}
                onChange={handleInputChange('neg')}
                label="Negozio"
              >
                {negozi.map((negozio) => (
                  <MenuItem key={negozio.neg} value={negozio.neg}>
                    {negozio.neg} - {negozio.negozio}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Nome Completo"
              value={formData.nome}
              onChange={handleInputChange('nome')}
              fullWidth
              required
              inputProps={{ maxLength: 100 }}
            />

            {editingDipendente && (
              <TextField
                label="Nome Utente"
                value={formData.username || ''}
                onChange={handleInputChange('username')}
                fullWidth
                inputProps={{ maxLength: 100 }}
              />
            )}

            <NumericInput
              label="Ore Settimanali"
              value={formData.oreSettimanali}
              onChange={(value) => setFormData({ ...formData, oreSettimanali: value })}
              min={1}
              max={48}
            />

            <NumericInput
              label="Livello"
              value={formData.livello}
              onChange={(value) => setFormData({ ...formData, livello: value })}
              min={1}
              max={10}
            />

            <TextField
              label="Data Assunzione"
              type="date"
              value={formatDateForInput(formData.dataAssunzione)}
              onChange={handleInputChange('dataAssunzione')}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Data Fine Contratto (opzionale)"
              type="date"
              value={formatDateForInput(formData.dataFineContratto || '')}
              onChange={handleInputChange('dataFineContratto')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Ore per Giorno di Lavoro:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <NumericInput
                  label="Lunedì"
                  value={formData.lunedi}
                  onChange={(value) => setFormData({ ...formData, lunedi: value })}
                />
                <NumericInput
                  label="Martedì"
                  value={formData.martedi}
                  onChange={(value) => setFormData({ ...formData, martedi: value })}
                />
                <NumericInput
                  label="Mercoledì"
                  value={formData.mercoledi}
                  onChange={(value) => setFormData({ ...formData, mercoledi: value })}
                />
                <NumericInput
                  label="Giovedì"
                  value={formData.giovedi}
                  onChange={(value) => setFormData({ ...formData, giovedi: value })}
                />
                <NumericInput
                  label="Venerdì"
                  value={formData.venerdi}
                  onChange={(value) => setFormData({ ...formData, venerdi: value })}
                />
                <NumericInput
                  label="Sabato"
                  value={formData.sabato}
                  onChange={(value) => setFormData({ ...formData, sabato: value })}
                />
                <NumericInput
                  label="Domenica"
                  value={formData.domenica}
                  onChange={(value) => setFormData({ ...formData, domenica: value })}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annulla</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{
              bgcolor: PAGE_COLOR,
              color: 'white',
              '&:hover': {
                bgcolor: '#E65100', // Versione più scura di #FF9800
              }
            }}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DipendenteList;
